import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { getExtensionKey, hasAuthScopeOverrides } from '../../../../state/helpers';
import { AuthorizeAction } from '../../actions/flexible/AuthorizeAction';
import { extractProvider } from '@atlaskit/link-extractors';
import UnauthorisedViewContent from '../../../common/UnauthorisedViewContent';
import { messages } from '../../../../messages';
import UnresolvedView from './unresolved-view';
import { withFlexibleUIBlockCardStyle } from './utils/withFlexibleUIBlockCardStyle';
const contentStyles = css({
  color: "var(--ds-text-subtlest, #626F86)",
  marginTop: "var(--ds-space-100, 0.5rem)",
  fontSize: '0.75rem'
});

/**
 * This view represents a Block card that has an 'Unauthorized' status .
 * It should have a "Connect" button that will allow a user to connect their account and view the block card.
 *
 * @see SmartLinkStatus
 * @see FlexibleCardProps
 */
const FlexibleUnauthorisedView = ({
  testId = 'smart-block-unauthorized-view',
  ...props
}) => {
  var _getExtensionKey, _cardState$details, _extractProvider;
  const {
    analytics,
    cardState,
    onAuthorize
  } = props;
  const extensionKey = (_getExtensionKey = getExtensionKey(cardState === null || cardState === void 0 ? void 0 : cardState.details)) !== null && _getExtensionKey !== void 0 ? _getExtensionKey : '';
  const data = (_cardState$details = cardState.details) === null || _cardState$details === void 0 ? void 0 : _cardState$details.data;
  const providerName = (_extractProvider = extractProvider(data)) === null || _extractProvider === void 0 ? void 0 : _extractProvider.text;
  const isProductIntegrationSupported = hasAuthScopeOverrides(cardState === null || cardState === void 0 ? void 0 : cardState.details);
  const handleAuthorize = useCallback(() => {
    if (onAuthorize) {
      analytics === null || analytics === void 0 ? void 0 : analytics.track.appAccountAuthStarted({
        extensionKey
      });
      onAuthorize();
    }
  }, [onAuthorize, extensionKey, analytics === null || analytics === void 0 ? void 0 : analytics.track]);
  const content = useMemo(() => onAuthorize ? jsx(UnauthorisedViewContent, {
    providerName: providerName,
    isProductIntegrationSupported: isProductIntegrationSupported,
    analytics: analytics,
    testId: testId
  }) : jsx(FormattedMessage, _extends({}, messages[providerName ? 'unauthorised_account_description' : 'unauthorised_account_description_no_provider'], {
    values: {
      context: providerName
    }
  })), [analytics, isProductIntegrationSupported, onAuthorize, providerName, testId]);
  const actions = useMemo(() => onAuthorize ? [AuthorizeAction(handleAuthorize, providerName)] : [], [handleAuthorize, onAuthorize, providerName]);
  return jsx(UnresolvedView, _extends({}, props, {
    actions: actions,
    testId: testId
  }), jsx("div", {
    css: contentStyles,
    "data-testid": `${testId}-content`
  }, content));
};
export default withFlexibleUIBlockCardStyle(FlexibleUnauthorisedView);